// Dismissible "add to home screen" prompt. Android/Chrome exposes
// beforeinstallprompt (native install); iOS needs manual Share -> Add to Home
// Screen instructions. Hidden once installed (standalone) or dismissed.
import { useEffect, useState } from "react";
import { isIos, isStandalone } from "../lib/pwa";

const DISMISS_KEY = "tpfl_install_dismissed";

interface BIPEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: string }>;
}

export function InstallNudge() {
  const [deferred, setDeferred] = useState<BIPEvent | null>(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (isStandalone() || localStorage.getItem(DISMISS_KEY)) return;

    const onBIP = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BIPEvent);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onBIP);

    // iOS never fires beforeinstallprompt — show manual instructions instead,
    // but only after a short delay so it isn't the first thing they see.
    let t: number | undefined;
    if (isIos()) t = window.setTimeout(() => setShow(true), 4000);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      if (t) clearTimeout(t);
    };
  }, []);

  if (!show) return null;

  function dismiss() {
    localStorage.setItem(DISMISS_KEY, "1");
    setShow(false);
  }

  async function install() {
    if (deferred) {
      await deferred.prompt();
      await deferred.userChoice;
      dismiss();
    }
  }

  return (
    <div className="fixed bottom-20 inset-x-0 z-30 px-4">
      <div className="max-w-md mx-auto bg-navy text-white rounded-2xl shadow-pop p-4 flex items-start gap-3">
        <i className="ph-fill ph-device-mobile text-xl mt-0.5" />
        <div className="flex-1 text-[13px]">
          <div className="font-medium">Add the Film Lab to your home screen</div>
          {isIos() && !deferred ? (
            <p className="text-white/80 mt-0.5">
              Tap the Share icon, then <b>Add to Home Screen</b> — it unlocks notifications too.
            </p>
          ) : (
            <p className="text-white/80 mt-0.5">Install for one-tap access and notifications.</p>
          )}
          {deferred && (
            <button onClick={install} className="mt-2 h-9 px-4 rounded-lg bg-white text-navy font-medium">
              Install
            </button>
          )}
        </div>
        <button onClick={dismiss} aria-label="Dismiss" className="text-white/70">
          <i className="ph ph-x" />
        </button>
      </div>
    </div>
  );
}
